import { useState, useRef, useEffect } from 'react'
import AutoResizeTextarea from './AutoResizeTextarea'
import {
  Video, Upload, Sparkles, Loader2, Clock,
  Film, Trash2, Star, Info, Type, Image, Link, Download, Plus, X
} from 'lucide-react'
import toast from 'react-hot-toast'
import client from '../api/client'
import { getCached, setCache } from '../utils/cache'

type TabMode = 't2v' | 'i2v' | 'v2v'

interface VideoModelInfo {
  id: string
  name: string
  provider: string
  color: string
  pricePerSec: number
  subType: string // text-to-video | image-to-video | video-to-video
}

const modelDurations: Record<string, number[]> = {
  // T2V: 15 сек
  'kling-video/v3/pro/text-to-video': [5, 10, 15],
  'kling-video/v3/standard/text-to-video': [5, 10, 15],
  'seedance-2.0/text-to-video': [5, 10, 15],
  'seedance-2.0/fast/text-to-video': [5, 10, 15],
  'seedance-1.0/pro/text-to-video': [5, 10],
  // T2V: 10 сек
  'kling-video/v2.6/pro/text-to-video': [5, 10],
  'kling-video/v2.5-turbo/pro/text-to-video': [5, 10],
  'veo3.1/text-to-video': [5, 10],
  'veo3.1/fast/text-to-video': [5, 10],
  'veo3.1/lite/text-to-video': [5, 10],
  // I2V: 15 сек
  'kling-video/v3/pro/image-to-video': [5, 10, 15],
  'kling-video/v3-turbo/pro/image-to-video': [5, 10, 15],
  'kling-video/v3/standard/image-to-video': [5, 10, 15],
  'seedance-2.0/image-to-video': [5, 10, 15],
  'seedance-2.0/fast/image-to-video': [5, 10, 15],
  'seedance-2.0/mini/image-to-video': [5, 10, 15],
  // I2V: 10 сек
  'kling-video/v2.6/pro/image-to-video': [5, 10],
  'kling-video/v2.5-turbo/pro/image-to-video': [5, 10],
  // V2V: 10 сек
  'kling-video/v3/pro/video-to-video': [5, 10],
  'kling-video/v3/video-to-video': [5, 10],
  'kling-video/v2.6/pro/video-to-video': [5, 10],
  'kling-video/v2.6/video-to-video': [5, 10],
}

const getDurations = (modelId: string): number[] =>
  modelDurations[modelId] || [5, 10, 15]

export default function VideoGenerator() {
  const [activeTab, setActiveTab] = useState<TabMode>('t2v')
  const [loading, setLoading] = useState(true)
  const [userStars, setUserStars] = useState(0)
  const [allModels, setAllModels] = useState<VideoModelInfo[]>(() => getCached<VideoModelInfo[]>('video_models') || [])
  const [prices, setPrices] = useState<Record<string, number>>(() => getCached<Record<string, number>>('video_prices') || {})

  // T2V
  const [t2vPrompt, setT2vPrompt] = useState('')
  const [t2vNegative, setT2vNegative] = useState('')
  const [t2vModel, setT2vModel] = useState('')

  // I2V
  const [i2vPrompt, setI2vPrompt] = useState('')
  const [i2vNegative, setI2vNegative] = useState('')
  const [i2vModel, setI2vModel] = useState('')
  const [i2vImages, setI2vImages] = useState<string[]>([]) // data urls, макс 1
  const i2vFileRef = useRef<HTMLInputElement>(null)

  // V2V
  const [v2vPrompt, setV2vPrompt] = useState('')
  const [v2vNegative, setV2vNegative] = useState('')
  const [v2vModel, setV2vModel] = useState('')
  const [v2vVideoUrl, setV2vVideoUrl] = useState('')
  const [v2vFile, setV2vFile] = useState<string | null>(null) // data url
  const v2vFileRef = useRef<HTMLInputElement>(null)

  // Duration (общая)
  const [duration, setDuration] = useState(5)

  const [isGenerating, setIsGenerating] = useState(false)
const [videos, setVideos] = useState<any[]>([])
const [totalVideos, setTotalVideos] = useState(0)
const [loadMoreLoading, setLoadMoreLoading] = useState(false)
const PAGE_SIZE = 8

  // Фильтрованные модели по вкладкам
  const t2vModels = allModels.filter((m) => m.subType === 'text-to-video')
  const i2vModels = allModels.filter((m) => m.subType === 'image-to-video')
  const v2vModels = allModels.filter((m) => m.subType === 'video-to-video')
  const hasV2v = v2vModels.length > 0

  // Если v2v-моделей нет — не держимся за вкладку
  if (!hasV2v && activeTab === 'v2v') {
    setActiveTab('t2v')
  }

  // Текущая модель для вкладки
  const currentModel = activeTab === 't2v' ? t2vModel
    : activeTab === 'i2v' ? i2vModel : v2vModel

  const getPrice = (modelId: string): number => prices[modelId] || 22
  const selectedPricePerSec = currentModel ? getPrice(currentModel) : 0
  const totalPrice = selectedPricePerSec * duration

  // Загрузка данных — из ai-models с display_name, ценами, порядком
  useEffect(() => {
    let cancelled = false
    Promise.all([
      client.get('/models/ai-models'),
      client.get('/admin/ai-models/calculated-prices').catch(() => ({ data: {} })),
      client.get('/models/my').catch(() => ({ data: {} })),
      client.get('/tokens/balance').catch(() => ({ data: { stars_balance: 0 } })),
    ]).then(([allRes, pricesRes, myRes, balanceRes]) => {
      if (cancelled) return

      const allData: any[] = allRes.data || []
      const rawPrices: Record<string, any> = pricesRes.data || {}

      // Цена в звёздах за секунду (из calculated-prices: input = stars_sec)
      const calcPrice = (m: any): number => {
        const p = rawPrices[m.model_name]
        return (typeof p === 'object' ? (p?.input ?? 22) : 22)
      }

      const videoPrices: Record<string, number> = {}
      for (const m of allData) {
        videoPrices[m.model_name] = calcPrice(m)
      }

      const userStars = balanceRes.data?.stars_balance ?? 0
      setUserStars(userStars)
      setPrices(videoPrices)
      setCache('video_prices', videoPrices)

      // Фильтруем видео-модели, сортируем по sort_order
      let videoModels = allData
        .filter((m: any) => m.category === 'video')
        .sort((a: any, b: any) => (a.sort_order ?? 999) - (b.sort_order ?? 999))

      // Применяем пользовательские настройки если есть
      const enabled = myRes.data?.video_models
      if (enabled && enabled.length > 0) {
        videoModels = videoModels.filter((m: any) => enabled.includes(m.model_name))
      }

      if (videoModels.length > 0) {
        const infos: VideoModelInfo[] = videoModels.map((m: any, idx: number) => {
          const id = m.model_name
          const name = m.display_name || id
          const provider = m.provider || 'Fal.ai'
          const price = videoPrices[id]
          const subType = m.category_sub === 't2v' ? 'text-to-video'
            : m.category_sub === 'i2v' ? 'image-to-video'
            : m.category_sub === 'v2v' ? 'video-to-video'
            : 'text-to-video'
          const color = 'text-indigo-400'
          return { id, name, provider, color, pricePerSec: price, subType }
        })
        setAllModels(infos)
        setCache('video_models', infos)
        // Устанавливаем первую модель для каждой вкладки
        const firstT2v = infos.find((m) => m.subType === 'text-to-video')
        const firstI2v = infos.find((m) => m.subType === 'image-to-video')
        const firstV2v = infos.find((m) => m.subType === 'video-to-video')
        if (firstT2v) setT2vModel(firstT2v.id)
        if (firstI2v) setI2vModel(firstI2v.id)
        if (firstV2v) setV2vModel(firstV2v.id)
      }
      setLoading(false)
    }).catch(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  // Polling для processing видео
  useEffect(() => {
    const interval = setInterval(async () => {
      const hasProcessing = videos.some((v) => v.status === 'processing' || v.status === 'pending' || v.status === 'in_progress')
      if (!hasProcessing) return
      try {
        const { data } = await client.get('/video/generations')
        if (Array.isArray(data) && data.length > 0) {
          setVideos(data)
          if (data.length > totalVideos) setTotalVideos(data.length)
        }
      } catch {}
    }, 5000)
    return () => clearInterval(interval)
  }, [videos])

  // Загрузка истории видео (пагинированная)
  const limitVideoHistory = async () => {
    try {
      const { data } = await client.get('/video/generations/paginated', { params: { limit: PAGE_SIZE, offset: 0 } })
      if (data?.items && data.items.length > 0) {
        setVideos(data.items)
      }
      setTotalVideos(data?.total || 0)
    } catch {}
  }

  useEffect(() => {
    limitVideoHistory()
  }, [])

  // Генерация
  const handleGenerate = async () => {
    const prompt = activeTab === 't2v' ? t2vPrompt
      : activeTab === 'i2v' ? i2vPrompt : v2vPrompt
    const negative = activeTab === 't2v' ? t2vNegative
      : activeTab === 'i2v' ? i2vNegative : v2vNegative

    if (!prompt.trim() || isGenerating || !currentModel) return
    if (userStars < totalPrice) {
      toast.error(`Недостаточно ⭐. Нужно ${totalPrice}, у вас ${userStars}`)
      return
    }

    setIsGenerating(true)
    try {
      // Автоперевод промпта на английский (API моделей требует английский)
      let finalPrompt = prompt.trim()
      let finalNegative = negative.trim()
      const hasCyrillic = /[а-яёА-ЯЁ]/.test(finalPrompt) || /[а-яёА-ЯЁ]/.test(finalNegative)
      if (hasCyrillic) {
        try {
          const [trP, trN] = await Promise.all([
            client.post('/translate', { text: finalPrompt }),
            finalNegative ? client.post('/translate', { text: finalNegative }) : Promise.resolve({ data: { translated: '' } }),
          ])
          finalPrompt = trP.data.translated || finalPrompt
          finalNegative = trN.data.translated || finalNegative
        } catch { /* оставляем оригинал */ }
      }

      const payload: any = {
        prompt: finalPrompt,
        model: currentModel,
        duration,
      }

      if (finalNegative) {
        payload.negative_prompt = finalNegative
      }

      if (activeTab === 'i2v') {
        payload.referenceImages = i2vImages.filter(Boolean)
      }

      if (activeTab === 'v2v') {
        if (v2vFile) {
          // Загружаем видео на сервер, чтобы получить публичный URL
          // Используем dataURI→blob напрямую (fetch блокируется CSP data:)
          const binary = atob(v2vFile.split(',')[1])
          const array = new Uint8Array(binary.length)
          for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i)
          const blob = new Blob([array], { type: v2vFile.split(',')[0].split(':')[1].split(';')[0] || 'video/mp4' })
          const uploadForm = new FormData()
          uploadForm.append('file', blob, 'video.mp4')
          const uploadResp = await client.post('/video/upload-video', uploadForm)
          payload.referenceVideoUrl = uploadResp.data.url
        } else if (v2vVideoUrl.trim()) {
          payload.referenceVideoUrl = v2vVideoUrl.trim()
        }
        // Для motion-control и других v2v моделей нужен image_url тоже
        if (i2vImages.length > 0) {
          payload.referenceImages = i2vImages.filter(Boolean)
        }
      }

      const { data } = await client.post('/video/generate', payload, { timeout: 300000 })
      setUserStars((prev) => Math.max(0, prev - totalPrice))
      setVideos((prev) => [data, ...prev])
      toast.success('Видео создаётся!')
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Ошибка при генерации. ⭐ Звёзды возвращены')
    } finally {
      setIsGenerating(false)
    }
  }

  // I2V загрузка одного изображения
  const handleI2vUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      if (ev.target?.result) {
        setI2vImages([ev.target.result as string])
      }
    }
    reader.readAsDataURL(file)
    // Сбрасываем input, чтобы можно было выбрать тот же файл повторно
    if (i2vFileRef.current) {
      i2vFileRef.current.value = ''
    }
  }

  // V2V загрузка видео
  const handleV2vUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || !files[0]) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      if (ev.target?.result) {
        setV2vFile(ev.target!.result as string)
        setV2vVideoUrl('')
      }
    }
    reader.readAsDataURL(files[0])
    e.target.value = ''
  }

  // Состояние для открытых селекторов (по вкладкам)
  const [openSelector, setOpenSelector] = useState<TabMode | null>(null)

  // Рендер кастомного селектора модели (как в ImageGenerator)
  const renderModelSelector = (models: VideoModelInfo[], selected: string, onChange: (id: string) => void, tab: TabMode) => {
    const selectedModel = models.find((m) => m.id === selected)
    if (models.length === 0) {
      return (
        <div className="text-xs text-gray-500 py-2">
          Нет доступных моделей для этого типа
        </div>
      )
    }
    return (
      <div className="relative">
        <button
          onClick={() => setOpenSelector(openSelector === tab ? null : tab)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl glass text-sm text-gray-300 hover:text-white transition-all w-full"
        >
          <Sparkles className="w-4 h-4 text-indigo-400 shrink-0" />
          <span className="truncate">{selectedModel?.name}</span>
          <span className="text-gray-600">·</span>
          <span className="text-gray-500 truncate">{selectedModel?.provider}</span>
          <span className="ml-auto text-indigo-400/70 text-xs font-mono">{selectedModel?.pricePerSec}⭐/с</span>
        </button>

        {openSelector === tab && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpenSelector(null)} />
            <div className="absolute top-full left-0 right-0 mt-2 bg-[#1a1a2e] border border-white/10 rounded-xl shadow-2xl z-50 p-2 space-y-0.5 max-h-80 overflow-y-auto whitespace-normal">
              {models.map((m) => (
                <button
                  key={m.id}
                  onClick={() => { onChange(m.id); setOpenSelector(null) }}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    selected === m.id
                      ? 'bg-indigo-500/20 text-indigo-300'
                      : 'text-gray-400 hover:bg-white/[0.06] hover:text-gray-200'
                  }`}
                >
                  <span className="font-medium">{m.name}</span>
                  <span className="ml-2 text-xs text-gray-500">{m.provider}</span>
                  <span className="ml-auto text-indigo-400/60 text-[10px] font-mono"> {m.pricePerSec}⭐/с</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    )
  }

  // Рендер промпта + негатива
  const renderPromptArea = (
    prompt: string,
    setPrompt: (v: string) => void,
    negative: string,
    setNegative: (v: string) => void,
    placeholder: string,
  ) => (
    <div className="space-y-3">
      <div>
        <label className="block text-xs text-gray-500 mb-1.5">Описание видео</label>
        <AutoResizeTextarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={placeholder}
        />
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1.5">Негативный промпт</label>
        <AutoResizeTextarea
          value={negative}
          onChange={(e) => setNegative(e.target.value)}
          placeholder="размытое, низкое качество, искажённые лица..."
          rows={1}
          style={{ minHeight: '36px', maxHeight: '260px' }}
        />
      </div>
    </div>
  )

  // Рендер селектора длительности
  const renderDurationSelector = () => { const availDurations = currentModel ? getDurations(currentModel) : [5, 10]; return (
    <div>
      <label className="block text-xs text-gray-500 mb-2 flex items-center gap-1">
        <Clock className="w-3 h-3" />
        Длительность
      </label>
      <div className="flex flex-wrap gap-2">
        {availDurations.map((d) => {
          const durPrice = selectedPricePerSec * d
          return (
            <button
              key={d}
              onClick={() => setDuration(d)}
              className={`
                px-4 py-1.5 rounded-lg text-xs transition-all
                ${duration === d
                  ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                  : 'glass text-gray-400 hover:text-gray-200'
                }
              `}
            >
              {d} сек
              {selectedPricePerSec > 0 && (
                <span className="ml-1 text-[9px] text-gray-600">({durPrice}⭐)</span>
              )}
            </button>
          )
        })}
      </div>
    </div>
    )
  }

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold gradient-text flex items-center gap-2">
            <Video className="w-6 h-6" />
            Генерация видео
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Создавайте видео из текста, фото или других видео
          </p>
        </div>
        <div className="glass px-3 py-1.5 rounded-xl text-xs flex items-center gap-1.5">
          <Star className="w-3.5 h-3.5 text-amber-400" />
          <span className="text-gray-300">{userStars}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-3">
        <button
          onClick={() => setActiveTab('t2v')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${
            activeTab === 't2v'
              ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/20'
              : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
          }`}
        >
          <Type className="w-4 h-4" />
          Текст → Видео
        </button>
        <button
          onClick={() => setActiveTab('i2v')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${
            activeTab === 'i2v'
              ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/20'
              : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
          }`}
        >
          <Image className="w-4 h-4" />
          Фото → Видео
        </button>
        {hasV2v && (
          <button
            onClick={() => setActiveTab('v2v')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${
              activeTab === 'v2v'
                ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/20'
                : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
            }`}
          >
            <Film className="w-4 h-4" />
            Видео → Видео
          </button>
        )}
      </div>

      {/* === TAB: Text → Video === */}
      {activeTab === 't2v' && (
        <div className="glass-card p-6 space-y-4">
          <div>
            <label className="block text-xs text-gray-500 mb-2">Модель AI</label>
            {renderModelSelector(t2vModels, t2vModel, setT2vModel, 't2v')}
          </div>
          {renderPromptArea(t2vPrompt, setT2vPrompt, t2vNegative, setT2vNegative, 'Кинематографичный кадр футуристического города ночью, неоновые огни, летающие машины, 4K...')}
          {renderDurationSelector()}
          <PriceInfo pricePerSec={selectedPricePerSec} duration={duration} total={totalPrice} userStars={userStars} />
          <GenerateButton onClick={handleGenerate} disabled={!t2vPrompt.trim() || isGenerating || !currentModel || userStars < totalPrice} totalPrice={totalPrice} isGenerating={isGenerating} />
        </div>
      )}

      {/* === TAB: Image → Video === */}
      {activeTab === 'i2v' && (
        <div className="glass-card p-6 space-y-4">
          <div>
            <label className="block text-xs text-gray-500 mb-2">Модель AI</label>
            {renderModelSelector(i2vModels, i2vModel, setI2vModel, 'i2v')}
          </div>
          {renderPromptArea(i2vPrompt, setI2vPrompt, i2vNegative, setI2vNegative, 'Анимируй это изображение, плавная панорама камеры, кинематографичное освещение...')}

          {/* Reference images upload */}
          <div>
            <label className="block text-xs text-gray-500 mb-2">Референс-изображение</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {[0].map((slot) => (
                <div key={slot} className="w-20 h-20 rounded-lg overflow-hidden relative group bg-white/5 border border-white/5">
                  {i2vImages[slot] ? (
                    <>
                      <img src={i2vImages[slot]} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => setI2vImages((prev) => { const n = [...prev]; delete n[slot]; return n })}
                        className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => { if (i2vFileRef.current) { i2vFileRef.current.click() } }}
                      className="w-full h-full flex items-center justify-center text-gray-500 hover:text-gray-300 transition-all"
                    >
                      <Upload className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <input
              ref={i2vFileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleI2vUpload}
            />
          </div>

          {renderDurationSelector()}
          <PriceInfo pricePerSec={selectedPricePerSec} duration={duration} total={totalPrice} userStars={userStars} />
          <GenerateButton onClick={handleGenerate} disabled={!i2vPrompt.trim() || isGenerating || !currentModel || userStars < totalPrice} totalPrice={totalPrice} isGenerating={isGenerating} />
        </div>
      )}

      {/* === TAB: Video → Video === */}
      {activeTab === 'v2v' && (
        <div className="glass-card p-6 space-y-4">
          <div>
            <label className="block text-xs text-gray-500 mb-2">Модель AI</label>
            {renderModelSelector(v2vModels, v2vModel, setV2vModel, 'v2v')}
          </div>
          {renderPromptArea(v2vPrompt, setV2vPrompt, v2vNegative, setV2vNegative, 'Переделай это видео в стиле аниме, плавное движение...')}

          {/* Reference video upload */}
          <div>
            <label className="block text-xs text-gray-500 mb-2">Исходное видео</label>
            <p className="text-[10px] text-gray-600 mb-2 leading-relaxed">до 30MB, не более одного персонажа. Для качественного результата используйте видео с одним чётким объектом.</p>
            <div className="space-y-2">
              {/* Upload from computer */}
              <div
                onClick={() => v2vFileRef.current?.click()}
                className="glass rounded-xl p-4 flex items-center justify-center gap-2 cursor-pointer hover:bg-white/5 transition-all"
              >
                <Upload className="w-5 h-5 text-gray-500" />
                <span className="text-xs text-gray-500">
                  {v2vFile ? 'Видео загружено' : 'Загрузить видео с компьютера'}
                </span>
              </div>
              <input
                ref={v2vFileRef}
                type="file"
                accept="video/*"
                className="hidden"
                onChange={handleV2vUpload}
              />

              {/* or URL input */}
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <Link className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" />
                  <input
                    type="text"
                    value={v2vVideoUrl}
                    onChange={(e) => { setV2vVideoUrl(e.target.value); setV2vFile(null) }}
                    placeholder="Или вставьте ссылку на видео..."
                    className="input-field pl-9 text-xs"
                  />
                </div>
              </div>

              {/* Preview of uploaded video */}
              {(v2vFile || v2vVideoUrl) && (
                <div className="relative rounded-xl overflow-hidden bg-black/40 max-w-sm">
                  {v2vFile ? (
                    <video src={v2vFile} className="w-full max-h-40 object-contain" controls />
                  ) : (
                    <div className="aspect-video flex items-center justify-center">
                      <video src={v2vVideoUrl} className="max-w-full max-h-full" controls />
                    </div>
                  )}
                  <button
                    onClick={() => { setV2vFile(null); setV2vVideoUrl('') }}
                    className="absolute top-2 right-2 p-1 bg-black/60 rounded-lg hover:bg-black/80 transition-all"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Reference images for v2v (motion-control) */}
          <div>
            <label className="block text-xs text-gray-500 mb-2">Референс-изображения (для motion-control)</label>
            <p className="text-[10px] text-gray-600 mb-2 leading-relaxed">Рекомендуется один персонаж. Motion-control лучше работает с изображениями, где есть один выраженный объект.</p>
            <div className="flex flex-wrap gap-2">
              {i2vImages.map((img, idx) => (
                <div key={idx} className="relative">
                  <img src={img} className="w-16 h-16 rounded-lg object-cover" />
                  <button
                    onClick={() => setI2vImages((p) => p.filter((_, i) => i !== idx))}
                    className="absolute -top-1.5 -right-1.5 p-0.5 bg-black/60 rounded-full hover:bg-red-500/80 transition-all"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {i2vImages.length < 3 && (
                <label className="w-16 h-16 rounded-lg border-2 border-dashed border-white/10 flex items-center justify-center cursor-pointer hover:border-indigo-500/30 transition-all">
                  <Plus className="w-4 h-4 text-gray-500" />
                  <input
                    ref={i2vFileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleI2vUpload}
                  />
                </label>
              )}
            </div>
          </div>

          {renderDurationSelector()}
          <PriceInfo pricePerSec={selectedPricePerSec} duration={duration} total={totalPrice} userStars={userStars} />
          <GenerateButton onClick={handleGenerate} disabled={!v2vPrompt.trim() || isGenerating || !currentModel || userStars < totalPrice} totalPrice={totalPrice} isGenerating={isGenerating} />
        </div>
      )}

      {/* Results */}
      {videos.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-300">Созданные видео</h2>
            <button
              onClick={async () => {
                try {
                  await client.delete('/video/generations')
                  setVideos([])
                  toast.success('Список очищен')
                } catch {
                  toast.error('Ошибка при очистке')
                }
              }}
              className="text-[11px] text-gray-500 hover:text-red-400 transition-colors"
            >
              Очистить список
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {videos.map((video: any) => (
              <div key={video.id} className="glass-card overflow-hidden">
                <div className="aspect-video bg-white/5 flex items-center justify-center relative">
                  {video.status === 'processing' || video.status === 'pending' ? (
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 text-indigo-400 animate-spin mx-auto mb-2" />
                      <p className="text-xs text-gray-400">Генерация...</p>
                    </div>
                  ) : video.url?.startsWith('data:') ? (
                    <div className="text-center p-4">
                      <img src={video.url} alt="" className="max-w-full max-h-full" />
                    </div>
                  ) : video.url ? (
                    <video src={video.url} controls className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 text-indigo-400 animate-spin mx-auto mb-2" />
                      <p className="text-xs text-gray-500">Обработка...</p>
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex gap-1.5">
                    {video.url && !video.url.startsWith('data:') && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation()
                          const resp = await fetch(video.url, { mode: 'cors' })
                          const blob = await resp.blob()
                          const link = document.createElement('a')
                          link.href = URL.createObjectURL(blob)
                          const ext = video.url.match(/\.(\w+)(\?|$)/)?.[1] || 'mp4'
                          link.download = `all-neurals-video-${video.id.slice(0, 8)}.${ext}`
                          link.click()
                          URL.revokeObjectURL(link.href)
                          toast.success('Скачивание начато')
                        }}
                        className="p-1.5 rounded-lg bg-black/50 hover:bg-black/70 text-white transition-colors"
                        title="Скачать"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={async (e) => {
                        e.stopPropagation()
                        try {
                          await client.delete(`/video/generations/${video.id}`)
                          setVideos((prev: any[]) => prev.filter((v: any) => v.id !== video.id))
                          toast.success('Видео удалено')
                        } catch {
                          toast.error('Ошибка при удалении')
                        }
                      }}
                      className="p-1.5 rounded-lg bg-black/50 hover:bg-red-500/70 text-white transition-colors"
                      title="Удалить"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="p-3">
                  <p className="text-xs text-gray-400 line-clamp-1">{video.prompt}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] text-gray-600">{video.model}</span>
                    <span className="text-[10px] text-gray-600">{video.tokens_spent}⭐</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {videos.length < totalVideos && (
            <div className="flex justify-center mt-4">
              <button
                onClick={async () => {
                  if (loadMoreLoading) return
                  setLoadMoreLoading(true)
                  try {
                    const { data } = await client.get('/video/generations/paginated', { params: { limit: PAGE_SIZE, offset: videos.length } })
                    if (data?.items) {
                      setVideos(prev => [...prev, ...data.items])
                    }
                  } catch {}
                  setLoadMoreLoading(false)
                }}
                className="px-6 py-3 rounded-xl glass text-sm text-gray-300 hover:text-white transition-all border border-white/10 hover:border-indigo-500/30"
              >
                {loadMoreLoading ? 'Загрузка...' : `Показать ещё (${videos.length} из ${totalVideos})`}
              </button>
            </div>
          )}
        </div>
      )}

      {allModels.length === 0 && (
        <div className="glass-card p-8 text-center">
          <Video className="w-12 h-12 mx-auto text-gray-600 mb-3" />
          <p className="text-gray-400 text-sm">Видео-модели пока недоступны</p>
          <p className="text-gray-600 text-xs mt-1">Добавьте их в админ-панели</p>
        </div>
      )}
    </div>
  )
}

// Компонент для отображения цены
function PriceInfo({ pricePerSec, duration, total, userStars }: { pricePerSec: number; duration: number; total: number; userStars: number }) {
  if (!pricePerSec) return null
  return (
    <div className="flex items-center gap-2 text-[11px] text-gray-500 bg-white/5 rounded-lg px-3 py-2">
      <Info className="w-3 h-3" />
      <span>
        {pricePerSec} ⭐/сек · {duration} сек = <span className="text-amber-400 font-medium">{total} ⭐</span>
        {userStars < total && <span className="text-red-400 ml-1">(недостаточно)</span>}
      </span>
    </div>
  )
}

// Компонент кнопки генерации
function GenerateButton({ onClick, disabled, totalPrice, isGenerating }: { onClick: () => void; disabled: boolean; totalPrice: number; isGenerating: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="gradient-btn w-full py-3 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-40"
    >
      {isGenerating ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          Создание видео...
        </>
      ) : (
        <>
          <Film className="w-4 h-4" />
          Создать видео — {totalPrice} ⭐
        </>
      )}
    </button>
  )
}
